import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Megaphone, Pencil, RefreshCw } from "lucide-react";
import { listAdSpend, previewAdSpend, saveAdSpend, deleteAdSpend, syncAdSpendFromMeta } from "@/lib/api";
import { numberLocale } from "@/lib/money";

// Shared ad spend (backend routes/ad_spend.py): one Meta campaign often covers several gyms, so the month's total
// is entered once and split by each gym's ad leads (cost per lead x the gym's leads). Each share is written as
// that gym's Ad Spend expense, which the Dashboard's cost per lead and Gym numbers read.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const fmt = (n, d = 0) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: d, minimumFractionDigits: d }).format(n || 0);
const SYMBOL = { INR: "₹", LKR: "LKR ", AED: "AED " };
const thisMonth = () => new Date().toISOString().slice(0, 7);
const blank = (month) => ({ id: null, month, name: "", amount: "", facility_ids: [], campaign_match: "" });

function SplitTable({ rows, cpl, total, cur }) {
  return (
    <table className="w-full text-sm mt-2">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-[0.16em] text-zinc-500 border-b border-[#1f1f1f]">
          <th className="py-2 font-medium">Gym</th>
          <th className="py-2 font-medium text-right">Ad leads</th>
          <th className="py-2 font-medium text-right">Ad Spend share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((g) => (
          <tr key={g.facility_id} className="border-b border-[#1a1a1a] last:border-0">
            <td className="py-2 text-zinc-200">{g.gym}</td>
            <td className="py-2 text-right tabular-nums">{fmt(g.leads)}</td>
            <td className="py-2 text-right tabular-nums">{cur}{fmt(g.share)}</td>
          </tr>
        ))}
        <tr>
          <td className="py-2 text-zinc-400">All {rows.length} gyms</td>
          <td className="py-2 text-right tabular-nums text-zinc-300">{fmt(total)}</td>
          <td className="py-2 text-right text-zinc-400">{cpl != null ? `${cur}${fmt(cpl, 2)} a lead` : "no leads yet"}</td>
        </tr>
      </tbody>
    </table>
  );
}

export default function AdSpend() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [form, setForm] = useState(blank(thisMonth()));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listAdSpend(month).then(setData).catch((e) => toast.error(err(e, "Couldn't load ad spend")));
  }, [month]);
  useEffect(() => { setData(null); load(); setForm(blank(month)); setPreview(null); }, [load, month]);

  const gyms = data?.gyms || [];
  const currency = useMemo(() => {
    const picked = gyms.filter((g) => form.facility_ids.includes(g.id));
    return picked[0]?.currency_code || null;
  }, [gyms, form.facility_ids]);
  const cur = SYMBOL[currency] || "";
  const toggle = (g) => {
    if (currency && g.currency_code !== currency && !form.facility_ids.includes(g.id)) {
      toast.error("One shared spend covers gyms in one currency only");
      return;
    }
    setForm((f) => ({ ...f, facility_ids: f.facility_ids.includes(g.id) ? f.facility_ids.filter((x) => x !== g.id) : [...f.facility_ids, g.id] }));
    setPreview(null);
  };
  const body = () => ({ month: form.month, name: form.name.trim(), amount: Number(form.amount), facility_ids: form.facility_ids,
    campaign_match: form.campaign_match.trim() || null });
  const ready = form.name.trim().length >= 2 && Number(form.amount) > 0 && form.facility_ids.length > 0;

  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await previewAdSpend(body())); } catch (e) { toast.error(err(e, "Couldn't work it out")); } finally { setBusy(false); }
  };
  const doSave = async () => {
    setBusy(true);
    try {
      await saveAdSpend(form.id, body());
      toast.success("Saved. Each gym's Ad Spend for the month is updated.");
      setForm(blank(month)); setPreview(null); load();
    } catch (e) { toast.error(err(e, "Couldn't save")); } finally { setBusy(false); }
  };
  const doMeta = async () => {
    setBusy(true);
    try {
      const r = await syncAdSpendFromMeta();
      if (r.skipped) toast.error("Meta isn't connected on the server yet");
      else toast.success("Updated from Meta");
      load();
    } catch (e) { toast.error(err(e, "Couldn't reach Meta")); } finally { setBusy(false); }
  };
  const doDelete = async (p) => {
    if (!window.confirm(`Remove ${p.name} for ${p.month}? Its Ad Spend comes off each gym.`)) return;
    try { await deleteAdSpend(p.id); toast.success("Removed"); load(); } catch (e) { toast.error(err(e, "Couldn't remove it")); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ad-spend-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5"><Megaphone className="h-3.5 w-3.5" />Across every gym</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Ad spend</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Each ad account&apos;s spend comes from Meta every night (hiring ads left out), or enter a month&apos;s spend by hand for the gyms it covers. FPA divides it by those gyms&apos; ad leads (cost per lead) and gives
            each gym its share (cost per lead × its leads) as that month&apos;s Ad Spend. It updates every night as leads come in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <button type="button" onClick={doMeta} disabled={busy || !data.meta?.connected} data-testid="ad-spend-meta-sync"
              title={data.meta?.connected ? "Pull this month's and last month's spend from Meta now" : "Add META_ACCESS_TOKEN on the server to connect Meta"}
              className="h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-50">
              <RefreshCw className="h-3.5 w-3.5" />
              {data.meta?.connected ? `Update from Meta${data.meta.last_sync ? ` · last ${new Date(data.meta.last_sync).toLocaleString()}` : ""}` : "Meta not connected"}
            </button>
          )}
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="ad-spend-month"
          className="bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100" />
        </div>
      </div>

      {!data && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading</div>}

      {data && data.pools.map((p) => {
        const s = p.last_split || {};
        const pc = SYMBOL[(gyms.find((g) => g.id === p.facility_ids[0]) || {}).currency_code] || "";
        const manual = data.manual_ad_spend.filter((m) => m.pool_id === p.id);
        return (
          <section key={p.id} className="card-surface p-4" data-testid="ad-spend-pool">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1">
                <div className="font-medium text-zinc-100">{p.name} · {pc}{fmt(p.amount)}</div>
                <div className="text-xs text-zinc-500">{p.source === "meta" ? "From Meta, updated every night · " : ""}{p.month}{p.campaign_match ? ` · only campaigns with "${p.campaign_match}"` : " · all ad leads"} · updated {s.at ? new Date(s.at).toLocaleString() : "never"}</div>
              </div>
              {p.source !== "meta" && <button type="button" className="text-zinc-400 hover:text-zinc-100" title="Edit"
                onClick={() => { setForm({ id: p.id, month: p.month, name: p.name, amount: String(p.amount), facility_ids: p.facility_ids, campaign_match: p.campaign_match || "" }); setPreview(null); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }}>
                <Pencil className="h-4 w-4" />
              </button>}
              <button type="button" className="text-zinc-500 hover:text-rose-300" title="Remove" onClick={() => doDelete(p)}><Trash2 className="h-4 w-4" /></button>
            </div>
            {s.gyms && <SplitTable rows={s.gyms} cpl={s.cost_per_lead} total={s.total_leads} cur={pc} />}
            {p.meta_left_out?.length > 0 && <p className="text-xs text-zinc-500 mt-2">Left out (not member ads): {p.meta_left_out.join(", ")}.</p>}
            {s.locked?.length > 0 && <p className="text-xs text-amber-200 mt-2">Locked by accounts, kept as it was: {s.locked.join(", ")}.</p>}
            {manual.length > 0 && (
              <p className="text-xs text-amber-200 mt-2">
                Ad Spend also entered by hand this month: {manual.map((m) => `${(gyms.find((g) => g.id === m.facility_id) || {}).name} ${pc}${fmt(m.amount)}`).join(", ")}.
                It adds on top of the split; remove it in Expenses if it&apos;s the same spend.
              </p>
            )}
          </section>
        );
      })}
      {data && !data.pools.length && <div className="card-surface p-6 text-sm text-zinc-400">No shared ad spend for {month} yet.</div>}

      {data && (
        <section className="card-surface p-4 space-y-3" data-testid="ad-spend-form">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{form.id ? "Edit shared spend" : "Add shared spend"} for {form.month}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setPreview(null); }} placeholder="Name, e.g. Bengaluru lead ads"
              className="bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100" data-testid="ad-spend-name" />
            <input type="number" value={form.amount} onChange={(e) => { setForm({ ...form, amount: e.target.value }); setPreview(null); }} placeholder="Total spent this month"
              className="bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100 font-mono" data-testid="ad-spend-amount" />
          </div>
          <div className="flex flex-wrap gap-2">
            {gyms.map((g) => (
              <button key={g.id} type="button" onClick={() => toggle(g)} data-testid={`ad-spend-gym-${g.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${form.facility_ids.includes(g.id) ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200" : "border-[#2a2a2a] text-zinc-400"}`}>
                {g.name}
              </button>
            ))}
          </div>
          <input value={form.campaign_match} onChange={(e) => { setForm({ ...form, campaign_match: e.target.value }); setPreview(null); }}
            placeholder="Optional: only count leads from campaigns with these words (e.g. Bengaluru). Leave empty for all ad leads."
            className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-zinc-100" />
          <div className="flex gap-2">
            <button type="button" disabled={!ready || busy} onClick={doPreview} className="h-9 px-4 rounded-full border border-[#2a2a2a] text-sm text-zinc-200 disabled:opacity-50"
              data-testid="ad-spend-preview">See the split</button>
            <button type="button" disabled={!ready || busy} onClick={doSave} className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm text-white disabled:opacity-50"
              data-testid="ad-spend-save">{busy ? "Working" : "Save and apply"}</button>
            {form.id && <button type="button" onClick={() => { setForm(blank(month)); setPreview(null); }} className="h-9 px-3 text-sm text-zinc-500">Cancel</button>}
          </div>
          {preview && <SplitTable rows={preview.gyms} cpl={preview.cost_per_lead} total={preview.total_leads} cur={cur} />}
        </section>
      )}
    </div>
  );
}
